import React, { useState } from 'react';
import { Menu, MenuItem } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
type PaymentStatusType =
    | 'unpaid'
    | 'paid'
    | 'partially_paid'
    | 'overpaid'
    // #2002 — derived from the money ledger
    | 'refunded'
    | 'partially_refunded'
    | 'disputed'
    | 'pending'
    // #1926 — not a payment state: an organization carries the booking under
    // its space agreement, so `payment_status` stays 'unpaid' on purpose.
    | 'covered'
    // PO, 2026-10-23 — not a payment state either: the booking's list price is
    // 0 €, so nothing was ever owed (`paymentBadgeStatus`, utils/bookingCoverage).
    | 'free';
type DamageReportType = 'yes' | 'no';
// #1993: three states, not two. `not_sent` means no mail ever went out;
// `change_not_sent` means one did, and a customer-relevant change was saved
// afterwards without telling them — the state a single boolean could not
// express, and the one that lets a customer turn up at the old time.
type MailStatusType = 'sent' | 'not_sent' | 'change_not_sent' | 'mail_blocked';
// `overtime` is emitted by the backend (`orderAdminHandler.ts`, a running
// sup/rental booking past its end) and has had a badge config below since
// #2423 — but it was never added to this union, so every surface typed
// against it was blind to a status the API actually sends. That is what
// made the overtime filter return nothing (#2417 F1).
export type BookingStatusType = 'active' | 'booked' | 'canceled' | 'finished' | 'reserved' | 'incompleted' | 'blocked' | 'conflicted' | 'expired' | 'overtime';

interface StatusOption {
    label: string;
    value: string;
}

interface StatusBadgeProps {
    type: BookingStatusType | PaymentStatusType | MailStatusType | DamageReportType | 'archived';
    showDropdown?: boolean;
    isEditing?: boolean;
    options?: StatusOption[];
    onSelect?: (value: string) => void;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ type, showDropdown = false, options = [], onSelect, isEditing }) => {
    const { t } = useTranslation();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const baseStatusConfig = {
        /* Booking status */
        active: {
            text: t('booking_details.status_badge.booking_status.active'),
            textColor: '#3F66FB',
            bgColor: '#CFD8FE',
            dotColor: '#3F66FB',
        },
        booked: {
            text: t('booking_details.status_badge.booking_status.booked'),
            textColor: '#2D912D',
            bgColor: '#CEEFCE',
            dotColor: '#2D912D',
        },
        canceled: {
            text: t('booking_details.status_badge.booking_status.canceled'),
            textColor: '#E94F54',
            bgColor: '#F8DFE5',
            dotColor: '#E94F54',
        },
        finished: {
            text: t('booking_details.status_badge.booking_status.finished'),
            textColor: '#515557',
            bgColor: '#DEDFDF',
            dotColor: '#515557',
        },
        reserved: {
            text: t('booking_details.status_badge.booking_status.reserved'),
            textColor: '#7C3AED',
            bgColor: '#EDE9FE',
            dotColor: '#7C3AED',
        },
        overtime: {
            text: t('booking_details.status.overtime'),
            textColor: '#B45309',
            bgColor: '#FEF3C7',
            dotColor: '#B45309',
        },
        incompleted: {
            text: t('booking_details.status_badge.booking_status.incompleted'),
            textColor: '#D97706',
            bgColor: '#FEF3C7',
            dotColor: '#D97706',
        },
        // Paid, the window passed, never used — the paid counterpart of
        // `incompleted`. Shares its amber palette on purpose: both say "this
        // slot went unused", which is the fact an operator is scanning for.
        expired: {
            text: t('booking_details.status_badge.booking_status.expired'),
            textColor: '#D97706',
            bgColor: '#FEF3C7',
            dotColor: '#D97706',
        },
        blocked: {
            text: t('booking_details.status_badge.booking_status.blocked'),
            textColor: '#991B1B',
            bgColor: '#FEE2E2',
            dotColor: '#991B1B',
        },
        conflicted: {
            text: t('booking_details.status_badge.booking_status.conflicted'),
            textColor: '#9D174D',
            bgColor: '#FCE7F3',
            dotColor: '#9D174D',
        },
        paid: {
            text: t('booking_details.status_badge.payment_status.paid'),
            textColor: '#2D912D',
            bgColor: '#CEEFCE',
            dotColor: '#2D912D',
        },
        // Grey from the theme's foreground token, not a raw hex: covered is
        // neither good nor bad news about money, so it carries no colour.
        covered: {
            text: t('bookings.bookings_table.covered_by_org'),
            textColor: 'rgb(var(--color-foreground))',
            bgColor: 'rgba(var(--color-foreground), 0.08)',
            dotColor: 'rgba(var(--color-foreground), 0.6)',
        },
        // Neutral like `covered`, for the same reason: free of charge is a
        // fact about the price, not good or bad news about money.
        free: {
            text: t('booking_details.status_badge.payment_status.free'),
            textColor: 'rgb(var(--color-foreground))',
            bgColor: 'rgba(var(--color-foreground), 0.08)',
            dotColor: 'rgba(var(--color-foreground), 0.6)',
        },
        unpaid: {
            text: t('booking_details.status_badge.payment_status.unpaid'),
            textColor: '#BE6A2B',
            bgColor: '#FCEEC6',
            dotColor: '#BE6A2B',
        },
        partially_paid: {
            text: t('booking_details.status_badge.payment_status.partially_paid'),
            textColor: '#BE6A2B',
            bgColor: '#FCEEC6',
            dotColor: '#BE6A2B',
        },
        overpaid: {
            text: t('booking_details.status_badge.payment_status.overpaid'),
            textColor: '#E94F54',
            bgColor: '#F8DFE5',
            dotColor: '#E94F54',
        },
        sent: {
            text: t('booking_details.mail_sent.mail_sent'),
            textColor: '#2D912D',
            bgColor: '#CEEFCE',
            dotColor: '#2D912D',
        },
        not_sent: {
            text: t('booking_details.mail_sent.mail_not_sent'),
            textColor: '#BE6A2B',
            bgColor: '#FCEEC6',
            dotColor: '#BE6A2B',
        },
        // Same warning family as `not_sent` on purpose: both mean the customer
        // is not fully informed, and the text carries the difference. A third
        // colour would imply a third severity that does not exist.
        // (Hex to match this file; it predates the theme-token rule and is
        // uniformly hardcoded — converting it is its own change, not this one.)
        change_not_sent: {
            text: t('booking_details.mail_sent.change_not_sent'),
            textColor: '#BE6A2B',
            bgColor: '#FCEEC6',
            dotColor: '#BE6A2B',
        },
        // The payment rule refused the mail (PO, 2026-10-30). Same warning
        // family; the reason and its fix link stand beside the badge
        // (`CustomerMailBlockedReason`).
        mail_blocked: {
            text: t('booking_details.mail_sent.mail_blocked'),
            textColor: '#BE6A2B',
            bgColor: '#FCEEC6',
            dotColor: '#BE6A2B',
        },
        yes: {
            text: t('booking_details.status_badge.booking_status.yes'),
            textColor: '#2D912D',
            bgColor: '#CEEFCE',
            dotColor: '#2D912D',
        },
        no: {
            text: t('booking_details.status_badge.booking_status.no'),
            textColor: '#E94F54',
            bgColor: '#F8DFE5',
            dotColor: '#E94F54',
        },
        archived: {
            text: t('bookings.archive.badge'),
            textColor: '#6B7280',
            bgColor: '#E5E7EB',
            dotColor: '#6B7280',
        },
    };

    // #2002: the derived money states. They reuse the palettes already in this
    // file rather than introducing new hex literals — this component predates the
    // theme tokens, and adding to that debt is not this change's job. Semantics:
    // a refund is closed business (neutral), a dispute is the most urgent thing
    // on the chip (same treatment as canceled), and pending/partial are in-flight.
    const statusConfig = {
        ...baseStatusConfig,
        refunded: {
            ...baseStatusConfig.finished,
            text: t('booking_details.status_badge.payment_status.refunded'),
        },
        partially_refunded: {
            ...baseStatusConfig.partially_paid,
            text: t('booking_details.status_badge.payment_status.partially_refunded'),
        },
        disputed: {
            ...baseStatusConfig.canceled,
            text: t('booking_details.status_badge.payment_status.disputed'),
        },
        pending: {
            ...baseStatusConfig.unpaid,
            text: t('booking_details.status_badge.payment_status.pending'),
        },
    };
    const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (isEditing) {
            if (showDropdown) {
                setAnchorEl(event.currentTarget);
            }
        }
    };

    const handleClose = (value?: string) => {
        setAnchorEl(null);
        if (value && onSelect) onSelect(value);
    };

    const status = statusConfig[type];

    return (
        <>
            <div
                onClick={handleClick}
                style={{ backgroundColor: status?.bgColor ?? '', color: status?.textColor ?? '' }}
                className={'inline-flex items-center rounded-full text-sm font-medium cursor-pointer pl-2 pr-2'}
            >
                <span
                    className={'inline-block w-2.5 h-2.5 min-w-[0.625rem] min-h-[0.625rem] rounded-full mr-2 capitalize'}
                    style={{ backgroundColor: status?.dotColor ?? '' }}
                />
                <span>{status?.text ?? ''}</span>
                {showDropdown && (
                    <span className={'ml-1'}>
                        <ExpandMoreIcon />
                    </span>
                )}
            </div>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => handleClose()}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                PaperProps={{
                    sx: {
                        mt: 1,
                        padding: '4px',
                        borderRadius: '8px',
                        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                    },
                }}
            >
                {options.map((option) => {
                    const optionConfig = statusConfig[option.value as keyof typeof statusConfig];
                    return (
                        <MenuItem
                            key={option.value}
                            onClick={() => handleClose(option.value)}
                            sx={{
                                padding: '8px 16px',
                                margin: '4px 8px',
                                borderRadius: '4px',
                                backgroundColor: optionConfig.bgColor,
                                color: optionConfig.textColor,
                                '&:hover': {
                                    backgroundColor: optionConfig.bgColor,
                                    opacity: 0.9,
                                },
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                minWidth: '150px',
                            }}
                        >
                            <span
                                style={{
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '50%',
                                    backgroundColor: optionConfig.dotColor,
                                }}
                            />
                            {option.label}
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
};

export default StatusBadge;
